import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontStyle, FontWeight, TextSize } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { UsernameBadges } from '@freelancer/ui/username';

@Component({
  selector: 'fl-comment-ui-content',
  template: `
    <div
      class="CommentText"
      [class.IsDisabled]="isDisabled"
      [flMarginBottom]="'xxsmall'"
    >
      <ng-content></ng-content>
    </div>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiContentComponent {
  @Input() isDisabled = false;
}

@Component({
  selector: 'fl-comment-ui-custom-user-title',
  template: `
    <fl-text
      [flMarginRight]="'xxsmall'"
      [size]="TextSize.XSMALL"
      [weight]="FontWeight.MEDIUM"
      [fontStyle]="disabled ? FontStyle.ITALIC : FontStyle.NORMAL"
    >
      <ng-content></ng-content>
    </fl-text>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiCustomUserTitleComponent {
  FontWeight = FontWeight;
  TextSize = TextSize;
  FontStyle = FontStyle;

  @Input() disabled = false;
}

@Component({
  selector: 'fl-comment-ui-options',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiOptionsComponent {}

@Component({
  selector: 'fl-comment-ui-edit-form',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiEditFormComponent {}

@Component({
  selector: 'fl-comment-ui-action',
  template: `
    <fl-button
      class="CommentAction"
      flTrackingLabel="flTrackingLabel"
      [disabled]="disabled"
      (click)="handleAction()"
    >
      <fl-icon
        *ngIf="icon"
        class="CommentAction-icon"
        [ngClass]="{
          'CommentAction-icon': true,
          'CommentAction-icon-offsetNegative':
            icon === 'ui-thumbs-down-outline',
          'CommentAction-icon-offsetPositive': icon === 'ui-thumbs-up-outline',
        }"
        [flMarginRight]="'xxxsmall'"
        [color]="active ? IconColor.SECONDARY : IconColor.FOREGROUND"
        [title]="label"
        [name]="icon"
        [size]="IconSize.SMALL"
      ></fl-icon>
      <p
        class="text-xxsmall CommentAction-text font-medium"
        i18n="FeedItemComment"
      >
        <ng-content></ng-content>
      </p>
    </fl-button>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiActionComponent {
  IconSize = IconSize;
  IconColor = IconColor;
  TextSize = TextSize;
  FontWeight = FontWeight;

  @Input() icon?: string;
  @Input() disabled = false;
  @Input() active = true;
  @Input() label?: string;
  @Input() flTrackingLabel: string;
  @Output() action = new EventEmitter<void>();

  handleAction(): void {
    this.action.emit();
  }
}

@Component({
  selector: 'fl-comment-ui-actions',
  template: `
    <div class="ActionsContainer">
      <ng-content select="fl-comment-ui-action"></ng-content>
    </div>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiActionsComponent {}

@Component({
  selector: 'fl-comment-ui',
  template: `
    <div class="CommentItem">
      <fl-user-avatar
        [flMarginRight]="'xxsmall'"
        [size]="AvatarSize.SMALL"
        [sizeDesktop]="AvatarSize.MID"
        [users]="[{ avatar: avatar, username: username }]"
      ></fl-user-avatar>
      <div class="CommentItem-content">
        <div
          class="CommentItem-header"
          [flMarginBottom]="isCompact || isEditMode ? 'xxsmall' : 'xxxsmall'"
        >
          <div
            class="CommentItem-header-details"
            [class.IsStacked]="isCompact"
            [class.IsDisabled]="isDisabled"
          >
            <fl-icon
              *ngIf="headerIcon"
              [flMarginRight]="'xxxsmall'"
              [matTooltip]="headerIconTooltip ?? ''"
              [matTooltipDisabled]="!headerIconTooltip"
              [name]="headerIcon"
              [size]="IconSize.SMALL"
            ></fl-icon>
            <ng-content
              *ngIf="useCustomUserTitle; else showUsername"
              select="fl-comment-ui-custom-user-title"
            ></ng-content>
            <ng-template #showUsername>
              <fl-username
                flTrackingLabel="CommentUsername"
                [flHideMobile]="hideDisplayNameMobile"
                [badges]="badges"
                [displayName]="displayName"
                [flMarginRight]="'xxsmall'"
                [link]="'/u/' + username"
                [username]="username"
              ></fl-username>
            </ng-template>
            <p
              class="text-xsmall font-bold"
              *ngIf="!isCompact"
              i18n="Text interpunct"
              [flMarginRight]="'xxsmall'"
            >
              ·
            </p>

            <fl-relative-time
              i18n-matTooltip="Comment created time"
              matTooltip="{{ timeCreated | date: 'EEEE, MMM d, y' }} at {{
                timeCreated | date: 'HH:mm a'
              }}"
              [matTooltipPosition]="'above'"
              [date]="timeCreated"
              [includeSeconds]="false"
              [strict]="true"
            ></fl-relative-time>
          </div>
          <div class="CommentItem-header-options">
            <ng-content select="fl-comment-ui-options"></ng-content>
          </div>
        </div>
        <ng-content
          *ngIf="!isEditMode; else editMode"
          select="fl-comment-ui-content"
        ></ng-content>
        <ng-template #editMode>
          <ng-content select="fl-comment-ui-edit-form"></ng-content>
        </ng-template>
        <ng-content
          *ngIf="actions"
          select="fl-comment-ui-actions"
        ></ng-content>
      </div>
    </div>
  `,
  styleUrls: ['comment-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentUiComponent {
  AvatarSize = AvatarSize;
  FontWeight = FontWeight;
  IconSize = IconSize;
  TextSize = TextSize;

  @Input() avatar?: string;
  @Input() displayName: string;
  // Hides the display name on mobile.
  @Input() hideDisplayNameMobile = false;
  @Input() headerIcon?: string;
  @Input() headerIconTooltip?: string;
  @Input() isCompact = false;
  @Input() isEditMode = false;
  @Input() timeCreated: number | string | Date;
  @Input() username: string;
  @Input() useCustomUserTitle = false;
  @Input() badges: UsernameBadges;
  @Input() isDisabled = false;
  @Input() actions = true;
}
